/* eslint-disable no-else-return */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import bindMethods from 'auto-bind';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import qs from 'query-string';
import styled from 'styled-components';

import ApplicationLayout from '../layouts/ApplicationLayout';
import ApplicationSpinner from '../layouts/ApplicationSpinner';
import FormField from '../components/FormField';
import FormSubmitButton from '../components/FormSubmitButton';
import { login } from '../api/sso';
import {
  addTokensLocalStorage,
  hasTokensLocalStorage,
  getIdTokenAndFavProductLocalStorage,
  isJwtValid,
  removeTokensLocalStorage,
} from '../utils/jwt';

const ForgotPasswordContainer = styled.div`
  margin-top: 30px;
`;

const ForgotPasswordLink = styled(Link)`
  font-size: 13px;
  color: ${props => props.theme.SECONDARY};
  text-decoration: none;
`;

const ErrorMessageText = styled.p`
  color: ${props => props.theme.PRIMARY};
  margin-bottom: 20px;
`;

class LoginPage extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);

    this.state = {
      displayError: false,
      displayLoginForm: false,
      isSubmitting: false,
      errorMessage: '',
      email: '',
      password: '',
    };
  }

  componentDidMount() {
    if (hasTokensLocalStorage()) {
      const { token, favoriteProduct } = getIdTokenAndFavProductLocalStorage();

      if (isJwtValid(token)) {
        return this.redirectWithToken(token, favoriteProduct);
      }

      removeTokensLocalStorage();
      return this.setState({ displayLoginForm: true });
    }

    return this.setState({ displayLoginForm: true });
  }

  async handleFormSubmit(event) {
    const { email, password } = this.state;
    event.preventDefault();

    this.setState({ isSubmitting: true }, async () => {
      try {
        const response = await login(email, password);
        addTokensLocalStorage(response);
        this.redirectWithToken(response.id_token, response.favoriteProduct);
      } catch (error) {
        const phrase = 'Something is wrong, please contact your System Administrator.';
        this.setState({
          isSubmitting: false,
          displayError: true,
          errorMessage:
            error.response.data.error.detail === phrase
              ? 'Login.Messages.ContactAdmin'
              : 'Login.Messages.WrongPassword',
        });
      }
    });
  }

  handleInputChange(event) {
    this.setState({
      [event.target.name]: event.target.value,
    });
  }

  redirectWithToken(token, favoriteProduct) {
    const {
      location: { search },
    } = this.props;
    const url = qs.parse(search);
    if (url.app_url) {
      return window.location.replace(`${url.app_url}?token=${token}`);
    }
    return window.location.replace(`${favoriteProduct}?token=${token}`);
  }

  render() {
    const { t, branding } = this.props;
    const {
      isSubmitting,
      displayError,
      displayLoginForm,
      email,
      password,
      errorMessage,
    } = this.state;

    if (isSubmitting) {
      return <ApplicationSpinner />;
    }

    if (displayLoginForm) {
      return (
        <ApplicationLayout branding={branding} formHeadline={t('Login.Text')}>
          {displayError && <ErrorMessageText>{t(errorMessage)}</ErrorMessageText>}
          <form id="SSO.Login.Form" onSubmit={this.handleFormSubmit}>
            <FormField
              inputId="SSO.Login.Email"
              name="email"
              type="email"
              value={email}
              placeholder={t('Placeholders.Email')}
              onChange={this.handleInputChange}
            />

            <FormField
              inputId="SSO.Login.Password"
              name="password"
              type="password"
              value={password}
              onChange={this.handleInputChange}
              placeholder={t('Placeholders.Password')}
            />

            <ForgotPasswordContainer>
              <ForgotPasswordLink to="/forgot" id="SSO.Login.ForgotPasswordLink">
                {t('ForgotPassword.Link')}
              </ForgotPasswordLink>
            </ForgotPasswordContainer>

            <FormSubmitButton id="SSO.Login.Submit">{t('Login.Text')}</FormSubmitButton>
          </form>
        </ApplicationLayout>
      );
    }

    return null;
  }
}

LoginPage.propTypes = {
  t: PropTypes.func.isRequired, // supplied from withTranslation
  location: PropTypes.object.isRequired, // supplied from React Router
  branding: PropTypes.string.isRequired,
};

export default withTranslation()(LoginPage);
